/**
 * Component 4: Process Map / Layout
 * SIPOC (Supplier, Input, Process, Output, Customer) analysis and file management
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus as PlusIcon,
  Trash2 as TrashIcon,
  Upload as UploadIcon,
  Eye as EyeIcon,
  Download as DownloadIcon,
  FileText as FileTextIcon,
  Image as ImageIcon,
  ArrowRight as ArrowRightIcon,
  Factory as FactoryIcon,
  Users as UsersIcon,
  Settings as SettingsIcon,
  Package as PackageIcon,
  Target as TargetIcon
} from 'lucide-react';
import type { KATABoardV2, SIPOCItem, FileAttachment } from '../../../types/kataBoardV2';
import { useKataBoardV2Store } from '../../../stores/kataBoardV2Store';
import { useAlert } from '../../../hooks/useAlert';
import { fileAttachmentApi } from '../../../services/kataBoardV2';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';
import ResizablePreviewModal from '../../common/FileAttachmentPreviewModal';

interface Component04Props {
  kataBoard: KATABoardV2;
}

interface NewSIPOCItem {
  text: string;
  description: string;
}

const Component04_ProcessMap: React.FC<Component04Props> = ({ kataBoard }) => {
  const { 
    updateKataBoard,
    fetchKataBoardByWorkshop
  } = useKataBoardV2Store();
  
  const { confirmDelete, AlertComponent } = useAlert();
  const canEdit = useKataBoardCanEdit();
  
  const [isEditingLocal, setIsEditingLocal] = useState(false);
  const [showAddSIPOC, setShowAddSIPOC] = useState<string | null>(null);
  const [newSIPOCItem, setNewSIPOCItem] = useState<NewSIPOCItem>({ text: '', description: '' });
  // File upload functionality
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local state for SIPOC data
  const [sipocData, setSipocData] = useState({
    suppliers: kataBoard.sipoc_suppliers || [],
    inputs: kataBoard.sipoc_inputs || [],
    processes: kataBoard.sipoc_processes || [],
    outputs: kataBoard.sipoc_outputs || [],
    customers: kataBoard.sipoc_customers || []
  });

  // Process map files (filter file attachments)
  const processMapFiles = kataBoard.file_attachments?.filter(
    file => file.attachment_type === 'process_map'
  ) || [];

  useEffect(() => {
    setSipocData({
      suppliers: kataBoard.sipoc_suppliers || [],
      inputs: kataBoard.sipoc_inputs || [],
      processes: kataBoard.sipoc_processes || [],
      outputs: kataBoard.sipoc_outputs || [],
      customers: kataBoard.sipoc_customers || []
    });
  }, [kataBoard]);

  const handleEdit = () => {
    if (!canEdit) return;
    setIsEditingLocal(true);
  };

  const handleSave = async () => {
    try {
      await updateKataBoard(kataBoard.id, {
        sipoc_suppliers: sipocData.suppliers,
        sipoc_inputs: sipocData.inputs,
        sipoc_processes: sipocData.processes,
        sipoc_outputs: sipocData.outputs,
        sipoc_customers: sipocData.customers
      });
      
      setIsEditingLocal(false);
      
      // Refresh the data
      await fetchKataBoardByWorkshop(kataBoard.workshop);
    } catch (error) {
      console.error('Failed to save SIPOC data:', error);
    }
  };

  const handleCancel = () => {
    // Reset to original data
    setSipocData({
      suppliers: kataBoard.sipoc_suppliers || [],
      inputs: kataBoard.sipoc_inputs || [],
      processes: kataBoard.sipoc_processes || [],
      outputs: kataBoard.sipoc_outputs || [],
      customers: kataBoard.sipoc_customers || []
    });
    setIsEditingLocal(false);
    setShowAddSIPOC(null);
  };

  const handleAddSIPOCItem = (category: string) => {
    if (!newSIPOCItem.text.trim()) return;

    const newItem: SIPOCItem = {
      id: Date.now().toString(),
      text: newSIPOCItem.text.trim(),
      description: newSIPOCItem.description.trim() || undefined
    };

    setSipocData(prev => ({
      ...prev,
      [category]: [...prev[category as keyof typeof prev], newItem]
    }));

    setNewSIPOCItem({ text: '', description: '' });
    setShowAddSIPOC(null);
  };

  const handleDeleteSIPOCItem = async (category: string, itemId: string, itemText: string) => {
    await confirmDelete(
      `SIPOC item "${itemText}"`,
      async () => {
        setSipocData(prev => ({
          ...prev,
          [category]: prev[category as keyof typeof prev].filter(item => item.id !== itemId)
        }));
      }
    );
  };

  // File upload functions
  const handleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    for (const file of fileArray) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }

      try {
        await fileAttachmentApi.create({
          kata_board: kataBoard.id,
          attachment_type: 'process_map',
          file: file,
          title: file.name,
          description: `Process map file uploaded for ${kataBoard.workshop_info?.title || 'workshop'}`
        });
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    // Refresh the data
    await fetchKataBoardByWorkshop(kataBoard.workshop);
    setShowFileUpload(false);
  };

  const handleDeleteFile = async (fileId: number, fileName: string) => {
    await confirmDelete(
      `file "${fileName}"`,
      async () => {
        try {
          await fileAttachmentApi.delete(fileId);
          await fetchKataBoardByWorkshop(kataBoard.workshop);
        } catch (error) {
          console.error('Failed to delete file:', error);
        }
      }
    );
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (!isEditingLocal) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const getSIPOCIcon = (category: string) => {
    switch (category) {
      case 'suppliers': return <FactoryIcon className="h-5 w-5" />;
      case 'inputs': return <PackageIcon className="h-5 w-5" />;
      case 'processes': return <SettingsIcon className="h-5 w-5" />;
      case 'outputs': return <TargetIcon className="h-5 w-5" />;
      case 'customers': return <UsersIcon className="h-5 w-5" />;
      default: return <TargetIcon className="h-5 w-5" />;
    }
  };

  const getSIPOCColor = (category: string) => {
    switch (category) {
      case 'suppliers': return { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-800', button: 'bg-purple-600 hover:bg-purple-700' };
      case 'inputs': return { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-800', button: 'bg-blue-600 hover:bg-blue-700' };
      case 'processes': return { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-800', button: 'bg-green-600 hover:bg-green-700' };
      case 'outputs': return { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-800', button: 'bg-orange-600 hover:bg-orange-700' };
      case 'customers': return { bg: 'bg-pink-100', border: 'border-pink-400', text: 'text-pink-800', button: 'bg-pink-600 hover:bg-pink-700' };
      default: return { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-800', button: 'bg-gray-600 hover:bg-gray-700' };
    }
  };

  const renderSIPOCSection = (category: string, items: SIPOCItem[], title: string) => {
    const colors = getSIPOCColor(category);
    const icon = getSIPOCIcon(category);

    return (
      <div key={category} className={`${colors.bg} ${colors.border} border-2 rounded-lg p-4`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className={`${colors.text}`}>
              {icon}
            </div>
            <h4 className={`font-semibold ${colors.text}`}>{title}</h4>
          </div>
          {isEditingLocal && (
            <button
              onClick={() => setShowAddSIPOC(category)}
              className={`p-1 rounded-md text-white ${colors.button}`}
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No {title.toLowerCase()} defined</p>
          ) : (
            items.map((item) => (
              <div key={item.id} className="flex items-start justify-between p-2 bg-white rounded border">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{item.text}</p>
                  {item.description && (
                    <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                  )}
                </div>
                {isEditingLocal && (
                  <button
                    onClick={() => handleDeleteSIPOCItem(category, item.id, item.text)}
                    className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add SIPOC Item Form */}
        {showAddSIPOC === category && (
          <div className="mt-3 p-3 bg-white rounded border-2 border-dashed border-gray-300">
            <div className="space-y-2">
              <input
                type="text"
                placeholder={`${title} name`}
                value={newSIPOCItem.text}
                onChange={(e) => setNewSIPOCItem({ ...newSIPOCItem, text: e.target.value })}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                autoFocus
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={newSIPOCItem.description}
                onChange={(e) => setNewSIPOCItem({ ...newSIPOCItem, description: e.target.value })}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowAddSIPOC(null)}
                  className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAddSIPOCItem(category)}
                  className={`px-3 py-1 text-xs text-white rounded ${colors.button}`}
                >
                  Add {title.slice(0, -1)}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Process Map / Layout</h2>
          <p className="text-sm text-gray-600 mt-1">
            SIPOC analysis and process documentation files
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {!isEditingLocal ? (
            canEdit ? (
            <button
              onClick={handleEdit}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              Edit
            </button>
            ) : null
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
              >
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>

      {/* SIPOC Flow Diagram */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">SIPOC Flow Diagram</h3>
        
        {/* Flow visualization */}
        <div className="flex items-center justify-center space-x-4 mb-8 overflow-x-auto">
          <div className="flex items-center space-x-4 min-w-max">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 border-2 border-purple-400 rounded-lg flex items-center justify-center mb-2">
                <FactoryIcon className="h-8 w-8 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-purple-800">Suppliers</p>
              <p className="text-xs text-gray-600">{sipocData.suppliers.length} items</p>
            </div>
            
            <ArrowRightIcon className="h-6 w-6 text-gray-400" />
            
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 border-2 border-blue-400 rounded-lg flex items-center justify-center mb-2">
                <PackageIcon className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-blue-800">Inputs</p>
              <p className="text-xs text-gray-600">{sipocData.inputs.length} items</p>
            </div>
            
            <ArrowRightIcon className="h-6 w-6 text-gray-400" />
            
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 border-2 border-green-400 rounded-lg flex items-center justify-center mb-2">
                <SettingsIcon className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-sm font-medium text-green-800">Process</p>
              <p className="text-xs text-gray-600">{sipocData.processes.length} steps</p>
            </div>
            
            <ArrowRightIcon className="h-6 w-6 text-gray-400" />
            
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-100 border-2 border-orange-400 rounded-lg flex items-center justify-center mb-2">
                <TargetIcon className="h-8 w-8 text-orange-600" />
              </div>
              <p className="text-sm font-medium text-orange-800">Outputs</p>
              <p className="text-xs text-gray-600">{sipocData.outputs.length} items</p>
            </div>
            
            <ArrowRightIcon className="h-6 w-6 text-gray-400" />
            
            <div className="text-center">
              <div className="w-20 h-20 bg-pink-100 border-2 border-pink-400 rounded-lg flex items-center justify-center mb-2">
                <UsersIcon className="h-8 w-8 text-pink-600" />
              </div>
              <p className="text-sm font-medium text-pink-800">Customers</p>
              <p className="text-xs text-gray-600">{sipocData.customers.length} items</p>
            </div>
          </div>
        </div>

        {/* SIPOC Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {renderSIPOCSection('suppliers', sipocData.suppliers, 'Suppliers')}
          {renderSIPOCSection('inputs', sipocData.inputs, 'Inputs')}
          {renderSIPOCSection('processes', sipocData.processes, 'Processes')}
          {renderSIPOCSection('outputs', sipocData.outputs, 'Outputs')}
          {renderSIPOCSection('customers', sipocData.customers, 'Customers')}
        </div>
      </div>

      {/* File Attachments */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Process Maps & Layout Files</h3>
          {isEditingLocal && (
            <button
              onClick={() => setShowFileUpload(true)}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              <UploadIcon className="h-4 w-4 mr-2" />
              Upload Files
            </button>
          )}
        </div>

        {/* File Upload Area */}
        {isEditingLocal && showFileUpload && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition-colors ${
              isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <UploadIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Drop files here or click to upload
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Supports images (PNG, JPG, GIF), PDFs, Word, Excel, PowerPoint (max 10MB each)
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
              className="hidden"
            />
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
              >
                Choose Files
              </button>
              <button
                onClick={() => setShowFileUpload(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Files List */}
        <div className="space-y-3">
          {processMapFiles.length === 0 ? (
            <div className="text-center py-8">
              <FileTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-sm text-gray-600">No process map files uploaded yet</p>
              {isEditingLocal && (
                <p className="text-xs text-gray-500 mt-1">
                  Upload process maps, layout diagrams, or related documents
                </p>
              )}
            </div>
          ) : (
            processMapFiles.map((file) => (
              <div key={file.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <span className="text-2xl">{fileAttachmentApi.getFileIcon(file.mime_type)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.title || file.original_filename}
                    </p>
                    {file.description && (
                      <p className="text-xs text-gray-600 truncate">{file.description}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      {(file.file_size / 1024 / 1024).toFixed(2)} MB • {file.mime_type}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {fileAttachmentApi.canPreview(file) && (
                    <button
                      onClick={() => setPreviewFile(file)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                      title="Preview file"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  )}
                  <a
                    href={fileAttachmentApi.getDownloadUrl(file)}
                    download={file.original_filename}
                    className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                    title="Download file"
                  >
                    <DownloadIcon className="h-4 w-4" />
                  </a>
                  {isEditingLocal && (
                    <button
                      onClick={() => handleDeleteFile(file.id, file.original_filename)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                      title="Delete file"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* File Preview Modal - Resizable */}
      {previewFile && (
        <ResizablePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}

      {/* Custom Alert Component */}
      {AlertComponent}
    </div>
  );
};

export default Component04_ProcessMap;
